import { differenceInMinutes, format } from "date-fns";
import parseISO from "date-fns/parseISO";
import { yearMonthParser } from "./parse.ts";
import { asUTC, fromUTC } from "./utc.ts";

export enum DateFormats {
  DATE = "MMM dd, yyyy",
  DATE_TIME = "MMM dd, yyyy hh:mmaaa",
  CHART_TICK_DATE_TIME = "MMM dd, yyyy h:mm a", // Just used to replace moment tick rendering
  TIME = "hh:mmaaa",
  YEAR = "yyyy",
  MONTH = "MMM yyyy",
  FULL_MONTH = "MMMM yyyy",
  YEAR_MONTH = "yyyyMM",
}

export const dateFormatter = (value: Date, convertToUTC = true) =>
  format(convertToUTC ? asUTC(value) : value, DateFormats.DATE);

export const datetimeFormatter = (value: Date) =>
  format(value, DateFormats.DATE_TIME);

export const chartTickDateTimeFormatter = (value: Date) =>
  format(value, DateFormats.CHART_TICK_DATE_TIME);

export const timeFormatter = (value: Date) => format(value, DateFormats.TIME);

export const yearFormatter = (value: Date) =>
  format(asUTC(value), DateFormats.YEAR);

export const monthFormatter = (value: Date) =>
  format(asUTC(value), DateFormats.MONTH);

export const fullMonthFormatter = (value: Date) =>
  format(asUTC(value), DateFormats.FULL_MONTH);

export const yearMonthFormatter = (value: Date) =>
  format(asUTC(value), DateFormats.YEAR_MONTH);

export const getDurationInMinutes = (end: Date, start: Date) => {
  return differenceInMinutes(new Date(end), new Date(start));
};

export const yearMonthName = (yearMonth: string) =>
  fullMonthFormatter(yearMonthParser(yearMonth));

export const yearMonthFromISO = (value: string) =>
  yearMonthFormatter(value ? fromUTC(parseISO(value)) : new Date());

export const getYearMonthFromDateRange = (value?: {
  from?: string | Date;
  to?: string | Date;
}) => (value?.from ? yearMonthFormatter(new Date(value.from)) : undefined);

/**
 * Formats a duration in seconds as a whole number of minutes, rounding up and
 * never going below one minute.
 *
 * @param seconds - Duration in seconds.
 * @returns A string like `"1 minute"` or `"5 minutes"`.
 */
export const formatSecondsAsRoundedUpMinutes = (seconds: number): string => {
  const minutes = Math.max(1, Math.ceil(seconds / 60));

  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
};
